import { NavLink } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import {
  hasCoPowers,
  FUND_DEFINITIONS,
  displayNameForUser,
  initialsForUser,
  type RagStatus,
} from '@cosvision/shared';
import { useAuthStore } from '../../store/auth-store.js';
import { useAiDrawerStore } from '../../store/ai-drawer-store.js';
import { useSidebarStore } from '../../store/sidebar-store.js';
import { useDashboardSummaryQuery } from '../../hooks/useDashboard.js';
import { useRegistrationsQuery } from '../../hooks/useAuth.js';
import { useInboxUnreadQuery } from '../../hooks/useMessages.js';
import { useTaskUpdatesNewCountQuery } from '../../hooks/useTaskUpdatesFeed.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import clsx from 'clsx';

const linkBase =
  'block rounded-md text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-bn-300';
const linkExpanded = 'px-3 py-2';
const linkCollapsed = 'p-2';

function navLinkClass(isExpanded: boolean) {
  return ({ isActive }: { isActive: boolean }): string =>
    clsx(
      linkBase,
      isExpanded ? linkExpanded : linkCollapsed,
      isActive ? 'bg-bn-700 text-white' : 'text-bn-100 hover:bg-bn-700/60 hover:text-white',
    );
}

const RAG_DOT: Record<RagStatus, { class: string; label: string }> = {
  GREEN: { class: 'bg-rag-green', label: 'healthy' },
  AMBER: { class: 'bg-rag-amber', label: 'caution' },
  RED: { class: 'bg-rag-red', label: 'attention needed' },
};

/**
 * Status dot beside each fund name in the sidebar. Colour matches the
 * dashboard tile's RAG state so the operator gets a glanceable health
 * view even on pages that aren't the dashboard. Decorative dot is
 * aria-hidden; the screen-reader-only span carries the textual state.
 */
function FundRagDot({ status }: { status: RagStatus | null }) {
  if (status === null) return null;
  const cfg = RAG_DOT[status];
  return (
    <>
      <span
        aria-hidden="true"
        className={clsx('inline-block h-1.5 w-1.5 shrink-0 rounded-full', cfg.class)}
      />
      <span className="sr-only"> · {cfg.label}</span>
    </>
  );
}

export type IconName =
  | 'dashboard'
  | 'entry'
  | 'fdr'
  | 'reports'
  | 'ai'
  | 'audit'
  | 'settings'
  | 'import'
  | 'broadcast'
  | 'people'
  | 'tasks'
  | 'board'
  | 'overview'
  | 'approvals'
  | 'inbox'
  | 'updates'
  | 'more';

export function NavIcon({ name }: { name: IconName }) {
  const cls = 'h-5 w-5 shrink-0';
  const sharedProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: cls,
    'aria-hidden': true,
  };
  switch (name) {
    case 'dashboard':
      return (
        <svg {...sharedProps}>
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      );
    case 'entry':
      return (
        <svg {...sharedProps}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4z" />
        </svg>
      );
    case 'fdr':
      return (
        <svg {...sharedProps}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18M7 14h2M11 14h2" />
        </svg>
      );
    case 'reports':
      return (
        <svg {...sharedProps}>
          <path d="M14 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V8z" />
          <path d="M14 3v5h5M9 13h6M9 17h6" />
        </svg>
      );
    case 'ai':
      return (
        <svg {...sharedProps}>
          <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case 'audit':
      return (
        <svg {...sharedProps}>
          <rect x="5" y="3" width="14" height="18" rx="2" />
          <path d="M8 7h8M8 11h8M8 15h6" />
        </svg>
      );
    case 'settings':
      return (
        <svg {...sharedProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 9.4l1.6-2-1-1.7-2.4.6a8 8 0 00-1.9-1.1L15 3h-2l-.7 2.2a8 8 0 00-1.9 1.1l-2.4-.6-1 1.7 1.6 2A8 8 0 008 11v2c0 .5.1 1 .2 1.4l-1.6 2 1 1.7 2.4-.6a8 8 0 001.9 1.1L13 21h2l.7-2.2a8 8 0 001.9-1.1l2.4.6 1-1.7-1.6-2c.1-.4.2-.9.2-1.4v-2c0-.5-.1-1-.2-1.4z" />
        </svg>
      );
    case 'import':
      return (
        <svg {...sharedProps}>
          <path d="M12 3v12" />
          <path d="M7 10l5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
      );
    case 'broadcast':
      return (
        <svg {...sharedProps}>
          <path d="M3 11v2a2 2 0 002 2h2l5 4V5L7 9H5a2 2 0 00-2 2z" />
          <path d="M17 8a5 5 0 010 8M20 5a8 8 0 010 14" />
        </svg>
      );
    case 'people':
      return (
        <svg {...sharedProps}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 20a6 6 0 0112 0" />
          <path d="M16 6a3 3 0 010 6M18 20a6 6 0 00-3-5.2" />
        </svg>
      );
    case 'tasks':
      return (
        <svg {...sharedProps}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M9 9l1.5 1.5L13 8M9 15l1.5 1.5L13 14" />
        </svg>
      );
    case 'approvals':
      return (
        <svg {...sharedProps}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 20a6 6 0 0111 0" />
          <path d="M16 12l2 2 4-4" />
        </svg>
      );
    case 'board':
      return (
        <svg {...sharedProps}>
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <path d="M9 4v16M15 4v16" />
        </svg>
      );
    case 'overview':
      return (
        <svg {...sharedProps}>
          <path d="M3 3v18h18" />
          <path d="M7 15l3-4 3 3 4-6" />
        </svg>
      );
    case 'inbox':
      return (
        <svg {...sharedProps}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
      );
    case 'updates':
      return (
        <svg {...sharedProps}>
          <path d="M8 3h8a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V5a2 2 0 012-2z" />
          <path d="M9 8h6M9 12h6M9 16h4" />
        </svg>
      );
    case 'more':
      return (
        <svg {...sharedProps}>
          <circle cx="5" cy="12" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="19" cy="12" r="1.6" />
        </svg>
      );
  }
}

interface NavRowProps {
  to: string;
  end?: boolean;
  icon: IconName;
  label: string;
  isExpanded: boolean;
  leadingExtra?: ReactNode;
  /** Optional unread/pending count chip (e.g. pending officer approvals). */
  badge?: number;
}

function NavRow({ to, end, icon, label, isExpanded, leadingExtra, badge }: NavRowProps) {
  const hasBadge = typeof badge === 'number' && badge > 0;
  return (
    <NavLink
      to={to}
      end={end}
      className={navLinkClass(isExpanded)}
      title={isExpanded ? undefined : label}
    >
      <span className={clsx('flex items-center', isExpanded ? 'gap-2' : 'justify-center')}>
        {leadingExtra}
        <span className="relative flex items-center">
          <NavIcon name={icon} />
          {!isExpanded && hasBadge && (
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rag-red ring-2 ring-bn-800"
            />
          )}
        </span>
        <span className={isExpanded ? 'flex-1' : 'sr-only'}>{label}</span>
        {isExpanded && hasBadge && (
          <span className="ml-auto rounded-full bg-rag-red px-1.5 py-0.5 text-[10px] font-semibold text-white">
            {badge}
          </span>
        )}
      </span>
    </NavLink>
  );
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const openAi = useAiDrawerStore((s) => s.open);
  const isCo = hasCoPowers(user);
  const isOperator = user?.role === 'DATA_ENTRY';
  const isOfficer = user?.role === 'OFFICER';
  const desktopExpanded = useSidebarStore((s) => s.isExpanded);
  const closeMobile = useSidebarStore((s) => s.closeMobile);
  const isMobileOpen = useSidebarStore((s) => s.isMobileOpen);
  const isDesktop = useIsDesktop();
  // Below `md` the sidebar is an off-canvas drawer and always renders in its
  // full labelled form; the icon-only rail (`desktopExpanded === false`) is a
  // desktop-only affordance.
  const isExpanded = isDesktop ? desktopExpanded : true;

  // Pending officer approvals drive the badge on the CO's Approvals nav row.
  const pendingQ = useRegistrationsQuery(isCo);
  const pendingCount = pendingQ.data?.length ?? 0;

  // Unread inbox total drives the badge on the Inbox nav row (CO + officers).
  const inboxQ = useInboxUnreadQuery(isCo || isOfficer);
  const inboxUnread = inboxQ.data?.total ?? 0;

  // CO-only — drives the badge on the Task Updates nav row (Phase 7D).
  const updatesNewQ = useTaskUpdatesNewCountQuery(isCo);
  const updatesNew = updatesNewQ.data ?? 0;

  // Only CO sees the fund list, so only CO triggers the query. The
  // dashboard already uses the same hook — TanStack Query dedupes so
  // there's no extra network call once the CO has visited the dashboard.
  const summaryQ = useDashboardSummaryQuery({ enabled: isCo });
  const fundRag = useMemo<Map<string, RagStatus>>(() => {
    const m = new Map<string, RagStatus>();
    for (const c of summaryQ.data?.cards ?? []) {
      m.set(c.fundCode.toUpperCase(), c.ragStatus);
    }
    return m;
  }, [summaryQ.data]);

  return (
    <aside
      // AppShell wraps the sidebar in `<div className="print-hide">`, so the
      // aside is a block child of a block div — `min-h-0` alone has no
      // parent height to bound against and the inner `<nav>` overflow never
      // engages. Pin to `h-screen` so the aside is self-sufficiently height-
      // bounded, then `min-h-0` lets the nav child be the actual scroll
      // container (header + footer stay pinned).
      className={clsx(
        // Mobile: fixed off-canvas drawer (full 18rem rail) that slides in
        // from the left. md+: in-flow relative rail whose width follows the
        // desktop collapse toggle. `transition-[transform,width]` animates the
        // slide on mobile and the width change on desktop.
        'fixed inset-y-0 left-0 z-50 flex h-screen min-h-0 w-72 flex-col bg-bn-800 text-bn-50 transition-[transform,width] duration-200 md:relative md:z-auto md:translate-x-0',
        // When parked off-canvas, drop pointer-events so the off-screen rail
        // (e.g. the logo) can't intercept taps on the header hamburger that
        // sits over the same coordinates. Restored on open and at md+.
        isMobileOpen
          ? 'translate-x-0'
          : '-translate-x-full pointer-events-none md:translate-x-0 md:pointer-events-auto',
        isExpanded ? 'md:w-64' : 'md:w-16',
      )}
    >
      <div
        className={clsx(
          'border-b border-bn-700/60',
          isExpanded ? 'px-4 py-5' : 'px-2 py-3 text-center',
        )}
      >
        {isExpanded ? (
          <div className="flex items-center gap-3">
            {/* White circular disc behind the crest — the logo's dark
                inner elements (the "11", radio waves) lose contrast on
                navy; the white backing brings every detail back. */}
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-white/40">
              <img
                src="/11sigbn-logo.png"
                alt=""
                aria-hidden="true"
                className="h-11 w-11 object-contain"
              />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-bn-200">11 Signal Bn</div>
              <div className="truncate text-base font-semibold leading-tight text-white">
                Odysseus
              </div>
            </div>
          </div>
        ) : (
          <span
            className="mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-white/40"
            title="Odysseus · 11 Signal Bn"
          >
            <img
              src="/11sigbn-logo.png"
              alt="11 Signal Battalion"
              className="h-8 w-8 object-contain"
            />
          </span>
        )}
      </div>

      <nav
        // On mobile, any tap inside the nav (link or button) bubbles here and
        // closes the drawer so the chosen destination is immediately visible.
        // No-op on desktop where the drawer is never open.
        onClick={closeMobile}
        className={clsx('flex-1 overflow-y-auto space-y-1', isExpanded ? 'px-3 py-4' : 'px-2 py-3')}
      >
        {isCo && <NavRow to="/" end icon="dashboard" label="Dashboard" isExpanded={isExpanded} />}
        {(isCo || isOperator) && (
          <NavRow to="/entry" icon="entry" label="Data Entry" isExpanded={isExpanded} />
        )}

        {isCo && (
          <>
            {isExpanded ? (
              <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-bn-300">
                Funds
              </div>
            ) : (
              <div className="my-2 border-t border-bn-700/60" aria-hidden="true" />
            )}
            {FUND_DEFINITIONS.map((f) => {
              const status = fundRag.get(f.code.toUpperCase()) ?? null;
              return (
                <NavLink
                  key={f.code}
                  to={`/funds/${f.code.toLowerCase()}`}
                  className={navLinkClass(isExpanded)}
                  title={isExpanded ? undefined : f.name}
                >
                  <span
                    className={clsx('flex items-center', isExpanded ? 'gap-2' : 'justify-center')}
                  >
                    <FundRagDot status={status} />
                    {isExpanded ? (
                      <span>{f.name}</span>
                    ) : (
                      <span className="text-[10px] font-bold tabular-nums">{f.code}</span>
                    )}
                  </span>
                </NavLink>
              );
            })}

            {isExpanded ? (
              <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-bn-300">
                Tools
              </div>
            ) : (
              <div className="my-2 border-t border-bn-700/60" aria-hidden="true" />
            )}
            <NavRow to="/fdr" icon="fdr" label="FDR / Savings" isExpanded={isExpanded} />
            <NavRow to="/entry/import" icon="import" label="Bulk Import" isExpanded={isExpanded} />
            <NavRow to="/reports" icon="reports" label="Reports" isExpanded={isExpanded} />
            <NavRow to="/co-orders" icon="broadcast" label="CO's Orders" isExpanded={isExpanded} />
            <NavRow to="/ai-knowledge" icon="ai" label="AI Knowledge" isExpanded={isExpanded} />
            <button
              type="button"
              onClick={openAi}
              title={isExpanded ? undefined : 'AI Co-Pilot'}
              className={clsx(
                linkBase,
                isExpanded ? linkExpanded : linkCollapsed,
                'w-full text-bn-100 hover:bg-bn-700/60 hover:text-white',
                isExpanded ? 'text-left' : '',
              )}
            >
              <span className={clsx('flex items-center', isExpanded ? 'gap-2' : 'justify-center')}>
                <NavIcon name="ai" />
                <span className={isExpanded ? '' : 'sr-only'}>AI Co-Pilot</span>
              </span>
            </button>
            <NavRow to="/audit" icon="audit" label="Audit Log" isExpanded={isExpanded} />
            <NavRow to="/settings" icon="settings" label="Settings" isExpanded={isExpanded} />
          </>
        )}

        {/* Command section — the officer corps surface. Visible to officers
            (My Work + People) and to the CO (People + Approvals). The
            financial sections above stay CO-only. */}
        {(isCo || isOfficer) && (
          <>
            {isExpanded ? (
              <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-bn-300">
                Command
              </div>
            ) : (
              <div className="my-2 border-t border-bn-700/60" aria-hidden="true" />
            )}
            {isOfficer && (
              <NavRow to="/my-work" icon="tasks" label="My Work" isExpanded={isExpanded} />
            )}
            <NavRow to="/tasks" icon="board" label="Boards" isExpanded={isExpanded} />
            <NavRow
              to="/messages"
              icon="inbox"
              label="Inbox"
              isExpanded={isExpanded}
              badge={inboxUnread}
            />
            {isCo && (
              <NavRow
                to="/overview"
                icon="overview"
                label="Command Overview"
                isExpanded={isExpanded}
              />
            )}
            {isCo && (
              <NavRow
                to="/task-updates"
                icon="updates"
                label="Task Updates"
                isExpanded={isExpanded}
                badge={updatesNew}
              />
            )}
            <NavRow to="/people" icon="people" label="People" isExpanded={isExpanded} />
            {isCo && (
              <NavRow
                to="/approvals"
                icon="approvals"
                label="Approvals"
                isExpanded={isExpanded}
                badge={pendingCount}
              />
            )}
          </>
        )}
      </nav>

      <div
        className={clsx(
          'border-t border-bn-700/60 text-xs text-bn-200',
          isExpanded ? 'px-5 py-4' : 'px-2 py-3 text-center',
        )}
      >
        {isExpanded ? (
          <>
            <div className="font-medium text-white truncate">
              {user ? displayNameForUser(user) : ''}
            </div>
            <div className="truncate">
              {user?.role === 'CO' ? 'Commanding Officer' : (user?.rank ?? user?.username)}
            </div>
          </>
        ) : (
          <div
            className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-bn-700 text-[11px] font-semibold text-white"
            title={user ? displayNameForUser(user) : ''}
          >
            {user ? initialsForUser(user) : '?'}
          </div>
        )}
      </div>
    </aside>
  );
}
